import React, { useEffect, useState, useContext } from "react";
// FullCalendarコンポーネント。
import FullCalendar, { DatesSetArg, EventContentArg } from "@fullcalendar/react";
// FullCalendarで週表示を可能にするモジュール。
import timeGridPlugin from "@fullcalendar/timegrid";
// FullCalendarで月表示を可能にするモジュール。
import dayGridPlugin from "@fullcalendar/daygrid";
// FullCalendarでリスト表示を可能にするモジュール。
import listPlugin from "@fullcalendar/list";
// FullCalendarで日付や時間が選択できるようになるモジュール。
import interactionPlugin from "@fullcalendar/interaction";
import { registerLocale } from "react-datepicker";
import ja from "date-fns/locale/ja";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import { useHistory } from "react-router";
import { Loading } from "pages/parts/Loading";
import { ConfirmModal } from "pages/parts/basicSettings/ConfirmModal";

import { BookingApi } from "api/BookingApi";
import { MailApi } from "api/MailApi";
import { UserApi } from "api/UserApi";
import { User } from "api/entities/User";
import { EventType } from "pages/statuses/EventType";
import { Mail } from "pages/statuses/Mail";
import { UserDetail } from "api/entities/UserDetail";
import { SubscribeDialog } from "pages/booking/SubscribeDialog";
import { ConfigGetter } from "utilities/ConfigGetter";
import * as ConfigKey from "utilities/ConfigGetter";
import * as CommonConstants from "utilities/CommonConstants";
import * as DbValueConstants from "utilities/DbValueConstants";
import { SelectBookingChangeOrDeleteDialog } from "pages/booking/SelectBookingChangeOrDeleteDialog";
import { ConfirmDialog } from "pages/booking/ConfirmDialog";
import { LaboratoryCautionsDialog } from "pages/booking/LaboratoryCautionsDialog";
import { SigninContext } from "pages/signin/SigninContext";
import { BookingUtil } from "pages/booking/BookingUtil";
import { DateUtil } from "utilities/DateUtil";
import { LiquidWasteBooking } from "api/entities/LiquidWasteBooking";
import AssignmentIcon from "@material-ui/icons/Assignment";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        buttons: {
            "& > *": {
                margin: theme.spacing(1),
            },
        },
        display: {
            display: `flex`,
        },
        box: {
            display: `flex`,
            width: `40px`,
            height: `20px`,
            backgroundColor: `#dae3f3`,
        },
        holiday: {
            backgroundColor: `#F3F3F3`,
        },
        bookable_days: {
            backgroundColor: `#dae3f3`,
        },
        // リスト表示のテーブル幅
        listTable_tdBookingType: {
            width: `200px`,
        },
        listTable_tdRequestTable: {
            width: `200px`,
        },
        listTable_th: {
            fontSize: `x-small`,
            border: `none !important`,
            textAlign: `left !important` as any,
        },
        listTable_td: {
            fontSize: `small`,
            border: `none !important`,
        },
        loading: {
            width: `20%`,
            margin: `0 auto`,
            marginTop: 10,
            position: `absolute`,
            top: `50%`,
            left: `50%`,
        },
        root: {
            width: `100%`,
            position: `relative`,
        },
        cautions_select: {
            color: `#7A903E`,
            marginTop: theme.spacing(2),
            marginButtom: theme.spacing(2),
            textAlign: `center`,
            fontSize: `large`,
        },
        cautions: {
            fontSize: `small`,
            marginTop: theme.spacing(1),
        },
    })
);

registerLocale(ConfigGetter.get(ConfigKey.CALENDAR_LOCALE), ja);

/**
 * カレンダー
 */
export const Calendar: React.FC = () => {
    const classes = useStyles();
    const history = useHistory();

    /**
     * 予定を追加する際にCalendarオブジェクトのメソッドを使用する必要がある。
     * (CalendarオブジェクトはRef経由でアクセスする必要がある。)
     */
    const ref = React.createRef<any>();

    const [events, setEvents] = useState<EventType[]>([]); // すべてのイベント情報
    // 表示モード（`dayGridMonth`：月、`timeGridWeek`：週、`timeGridDay`：日、`listMonth`：週）
    const [viewMode, setViewMode] = useState<string>(() => {
        const mode = sessionStorage.getItem(CommonConstants.SESSION_KEY_VIEW_MODE);
        if (mode !== null) {
            return mode;
        }
        return CommonConstants.VIEWMODE_DAY_GRID_MONTH;
    });
    // 対象の予約
    const [targetBooking, setTargetBooking] = useState<EventType>({
        id: ``,
        start: new Date(),
        end: new Date(),
        classNames: [],
        groupId: ``,
        bookingTypeId: 0,
        status: 0,
        subscriberFaculty: ``,
        subscriberDepartment: ``,
        subscriberName: ``,
        subscriberLaboratoryId: ``,
        subscriberLaboratoryName: ``,
        subscriberMailAddress: ``,
        subscriberTel: ``,
        subscriberMemo: ``,
        numberOfChemicals: 0,
        depositBookingDetails: [],
        liquidWasteBookingDetails: [],
        solidWasteBookingDetails: [],
    });
    // 研究室に紐づく対象の予約
    const [targetBookingForLaboratoryCheck, setTargetBookingForLaboratoryCheck] = useState<EventType>({
        id: ``,
        start: new Date(),
        end: new Date(),
        classNames: [],
        groupId: ``,
        bookingTypeId: 0,
        status: 0,
        subscriberFaculty: ``,
        subscriberDepartment: ``,
        subscriberName: ``,
        subscriberLaboratoryId: ``,
        subscriberLaboratoryName: ``,
        subscriberMailAddress: ``,
        subscriberTel: ``,
        subscriberMemo: ``,
        numberOfChemicals: 0,
        depositBookingDetails: [],
        liquidWasteBookingDetails: [],
        solidWasteBookingDetails: [],
    });
    // 空予約に採番している一時的なID（登録後にイベントから削除するために使用）
    const [freeEventId, setFreeEventId] = useState<string>(``);

    // モーダル
    const [isOpenForConfirmModal, setOpenForConfirmModal] = useState<boolean>(false);
    const [messageForConfirmModal, setMessageForConfirmModal] = useState<string>(``);

    // ダイアログ
    const [isOpenForSubscribeDialog, setOpenForSubscribeDialog] = useState<boolean>(false);
    const [isOpenForSelectBookingChangeorDeleteDialog, setOpenForSelectBookingChangeorDeleteDialog] = useState<boolean>(
        false
    );
    const [isOpenForConfirmDialog, setOpenForConfirmDialog] = useState<boolean>(false);
    const [isOpenForLaboratoryCautionsDialog, setOpenForLaboratoryCautionsDialog] = useState<boolean>(false);
    // ローディング
    const [isLoading, setLoading] = useState<boolean>(false);
    // ステッパー
    const [activeStep, setActiveStep] = useState(CommonConstants.SELECT_BOOKING_TIME_DIAOLG);

    // 表示期間
    const [startViewDate, setStartViewDate] = useState<Date>(() => {
        const sessionDate = sessionStorage.getItem(CommonConstants.SESSION_KEY_START_VIEW_DATE);
        if (sessionDate !== null) {
            return new Date(sessionDate);
        }
        const date: Date = new Date();
        date.setDate(1);
        return date;
    });
    const [endViewDate, setEndViewDate] = useState<Date>(() => {
        const sessionDate = sessionStorage.getItem(CommonConstants.SESSION_KEY_END_VIEW_DATE);
        if (sessionDate !== null) {
            return new Date(sessionDate);
        }
        const date: Date = new Date();
        date.setMonth(date.getMonth() + 1);
        date.setDate(0);
        return date;
    });
    // カレンダーに表示する最初の日にち
    const [initialDate] = useState<Date>(() => {
        let date: Date;
        const sessionDate = sessionStorage.getItem(CommonConstants.SESSION_KEY_START_VIEW_DATE);
        if (sessionDate === null) {
            date = new Date();
            date.setDate(1);
        } else {
            date = new Date(sessionDate);
        }

        if (viewMode === CommonConstants.VIEWMODE_DAY_GRID_MONTH && date.getDate() !== 1) {
            date.setDate(1);
            date.setMonth(date.getMonth() + 1);
        }
        return date;
    });

    // エラーメッセージ
    const [errorMessage, setErrorMessage] = useState<string>(``);
    const { success } = useContext(SigninContext);

    useEffect(() => {
        const execute = async () => {
            // ローディング開始
            setLoading(true);

            // 予約情報を初期化
            if (ref.current !== null) {
                ref.current.getApi().removeAllEvents();
            }

            // 予約情報を取得
            const result = await BookingApi.getBooking(startViewDate, endViewDate);
            if (result?.status === 200) {
                const bookingEntities: LiquidWasteBooking[] = Array.from(result?.data.liquidWasteBookings);
                const bookings = convertBookings(bookingEntities);
                // カレンダーに表示するイベント（予約、休日）を設定
                setAllEvents(bookings);
            }

            // ローディング終了
            setLoading(false);
        };
        execute();
        setErrorMessage(``);
        // eslint-disable-next-line
    }, [viewMode, startViewDate, endViewDate, success]);

    /**
     * DBから取得した予約情報を、イベント情報として持ち回せるよう詰め替える(予約情報複数件の場合)
     * @param bookingEntities DBから取得した予約情報
     * @return 予約イベント
     */
    const convertBookings = (bookingEntities: LiquidWasteBooking[]): EventType[] => {
        const bookings: EventType[] = [];
        bookingEntities.forEach((entity) => {
            bookings.push(convertBooking(entity));
        });
        return bookings;
    };

    /**
     * カレンダーに表示するイベントを設定する
     * @param bookings 予約イベント
     */
    const setAllEvents = async (bookings: EventType[]) => {
        // カレンダーに表示する予約、休日情報を設定
        const events: EventType[] = await getBookingEvents(bookings);
        events.push(...getHolidayEvents());

        setEvents(events);
    };

    /**
     * カレンダーに表示する予約イベントを取得
     * @param bookings 予約イベント
     * @return 予約イベント
     */
    const getBookingEvents = async (bookingsOrg: EventType[]): Promise<EventType[]> => {
        if (getViewMode() !== CommonConstants.VIEWMODE_LIST_WEEK) {
            // リスト表示以外の場合は、イベントに予約情報を持たせない
            return [];
        }

        // ログインユーザ情報取得
        const user: User = await UserApi.getUser();
        if (!user.signin) {
            // 未ログインの場合は、イベントに予約情報を持たせない
            return [];
        }

        // ログイン済みの場合、自身が所属する研究室の予約をイベントに追加
        return bookingsOrg.filter((row) => row.subscriberLaboratoryId === user.laboid);
    };

    /**
     * 表示モードを取得
     */
    const getViewMode = () => {
        let mode;
        if (ref.current !== null) {
            // refがある場合は、refから取得
            mode = ref.current.getApi().view.type;
        } else {
            // refがない場合は、セッションから取得
            mode = sessionStorage.getItem(CommonConstants.SESSION_KEY_VIEW_MODE);
            if (mode != null) {
                return mode;
            }
            // デフォルト値
            mode = CommonConstants.VIEWMODE_DAY_GRID_MONTH;
        }

        return mode;
    };

    /**
     * 休日情報を取得
     * （初回のみプロパティから取得し、次回以降はsessionストレージから取得する）
     * @return 休日情報
     */
    const getHolidays = (): string[] => {
        let holidays: string[] = [];
        // 休日情報を即時反映するため、常にプロパティから取得(ストレージにも念のため格納しておく)
        // if (sessionStorage.getItem(CommonConstants.SESSION_KEY_HOLIDAYS) === null) {
            sessionStorage.setItem(
                CommonConstants.SESSION_KEY_HOLIDAYS,
                JSON.stringify(ConfigGetter.get(ConfigKey.CAMPUS_HOLIDAY))
            );
        // } else {
            const holidaysJson = sessionStorage.getItem(CommonConstants.SESSION_KEY_HOLIDAYS);
            if (typeof holidaysJson === `string`) {
                holidays = JSON.parse(holidaysJson);
            }
        // }
        return holidays;
    };

    /**
     * 休日情報を取得
     * @return 休日イベント
     */
    const getHolidayEvents = (): EventType[] => {
        const holidays: string[] = getHolidays();
        const events: EventType[] = [];

        // 表示期間内の休日情報を設定
        if (holidays.length > 0) {
            holidays.forEach((date: string) => {
                const start = new Date(date);
                const end = new Date(date);
                end.setDate(end.getDate() + 1);
                if (start.getTime() >= startViewDate.getTime() && start.getTime() <= endViewDate.getTime()) {
                    events.push({
                        id: ``,
                        start: start,
                        end: end,
                        classNames: [classes.holiday],
                        groupId: CommonConstants.GROUP_ID_HOLIDAY,
                        liquidWasteBookingDetails: [],
                        depositBookingDetails: [],
                        solidWasteBookingDetails: [],
                    });
                }
            });
        }

        return events;
    };

    /**
     * 空予約情報を取得
     * @param bookings 予約イベント
     * @return 空予約イベント
     */
    const getFreeBookingEvents = (bookings: EventType[], date: Date): EventType[] => {
        const events: EventType[] = [];
        // 予約が入っていない時間帯に対して、動的に空予約を作る
        events.push(
            ...createFreeBookingEventsByBookingType(bookings, date, DbValueConstants.BOOKING_TYPE_ID_LIQUID_WASTE)
        );
        events.push(...createFreeBookingEventsByBookingType(bookings, date, DbValueConstants.BOOKING_TYPE_ID_DEPOSIT));

        return events;
    };

    /**
     * 予約種別IDごとに空予約を作成する
     * @param bookings 予約イベント
     * @param date 選択日時
     * @param bookingTypeId 予約種別ID
     * @return 空予約イベント
     */
    const createFreeBookingEventsByBookingType = (bookings: any[], date: Date, bookingTypeId: number): EventType[] => {
        // 予約時間
        const startTime = ConfigGetter.getNum(ConfigKey.BOOKING_START_TIME);
        const endTime = ConfigGetter.getNum(ConfigKey.BOOKING_END_TIME);
        const interval = getInterval(bookingTypeId);
        const events: EventType[] = [];

        // 予約開始時間を設定
        const bookableStartTime = new Date(date);
        bookableStartTime.setHours(startTime);
        bookableStartTime.setMinutes(0);
        bookableStartTime.setSeconds(0);
        bookableStartTime.setMilliseconds(0);

        let start = new Date(bookableStartTime);
        let end;

        let id = 0;
        const bookedBookings = bookings.filter((row) => DateUtil.parseDate(row.start) === DateUtil.parseDate(date));
        // 空予約作成不可の場合
        if (!isBookableDate(date)) {
            return events;
        }

        while (start.getHours() < endTime) {
            if (isBooked(start, bookingTypeId, bookedBookings)) {
                // 予約済みの場合は予約不可
                start.setMinutes(start.getMinutes() + interval);
                continue;
            }

            end = new Date(start);
            end.setMinutes(end.getMinutes() + interval);
            events.push({
                id: `${bookingTypeId}_${id}`,
                start: start,
                end: end,
                classNames: [`${BookingUtil.getClassNameByBookingType(bookingTypeId)}`],
                groupId: CommonConstants.GROUP_ID_FREE,
                extendedProps: {
                    // eventから取得できるように予約種別を保持
                    bookingTypeId: bookingTypeId,
                },
                bookingTypeId: bookingTypeId,
                status: 0,
                liquidWasteBookingDetails: [],
                depositBookingDetails: [],
                solidWasteBookingDetails: [],
            });
            start = new Date(end);
            id++;
        }
        return events;
    };

    /**
     * 予約済みかどうか判定
     * @param start 予約開始日時
     * @param bookings 予約種別ID
     * @param bookings 予約情報
     * @return 予約済みの場合true
     */
    const isBooked = (start: Date, bookingTypeId: number, bookings: EventType[]): boolean => {
        for (let i = 0; i < bookings.length; i++) {
            if (bookings[i].start.getTime() === start.getTime() && bookings[i].bookingTypeId === bookingTypeId) {
                return true;
            }
        }
        return false;
    };

    /**
     * 予約種別IDに紐づく予約間隔時間を取得
     * @param bookingTypeId 予約種別ID
     * @return 予約間隔時間
     */
    const getInterval = (bookingTypeId: number): number => {
        let interval = 0;
        switch (bookingTypeId) {
            case 1:
                interval = ConfigGetter.getNum(ConfigKey.BOOKING_INTERVAL_LIQUID_WASTE);
                break;
            case 2:
                interval = ConfigGetter.getNum(ConfigKey.BOOKING_INTERVAL_DEPOSIT);
                break;
            default:
                break;
        }
        return interval;
    };

    /**
     * 日時から予約情報を作成（新規登録用）
     * @param date
     * @return 予約情報
     */
    const createInitializeBooking = (date: any): EventType => {
        return {
            id: ``,
            start: date,
            end: new Date(),
            groupId: CommonConstants.GROUP_ID_FREE,
            bookingTypeId: 0,
            liquidWasteBookingDetails: [],
            depositBookingDetails: [],
            solidWasteBookingDetails: [],
        };
    };

    /**
     * カレンダーの日付（or空白）がクリックされた場合に行う処理
     * @param arg 選択された日付情報
     */
    const handleDateClick = (arg: any) => {
        setActiveStep(CommonConstants.SELECT_BOOKING_TIME_DIAOLG);
        setErrorMessage(``);

        // 予約詳細情報を取得
        const executeAboutBookingdetail = async () => {
            // ローディング開始
            setLoading(true);

            let date;
            // 空白を押下した場合
            if (arg.date !== undefined) {
                date = arg.date;
            } else {
                // 日付を押下した場合
                date = arg;
            }

            // 休日の場合、予約不可能な曜日の場合は何もしない
            if (isHoliday(date) || !isBookableDate(date)) {
                // ローディング終了
                setLoading(false);
                return;
            }

            // 選択日に、ログインしている研究室での予約があるか確認
            // 初期値設定
            setTargetBookingForLaboratoryCheck({
                id: ``,
                start: new Date(),
                end: new Date(),
                classNames: [],
                groupId: ``,
                bookingTypeId: 0,
                status: 0,
                subscriberFaculty: ``,
                subscriberDepartment: ``,
                subscriberName: ``,
                subscriberLaboratoryId: ``,
                subscriberLaboratoryName: ``,
                subscriberMailAddress: ``,
                subscriberTel: ``,
                subscriberMemo: ``,
                numberOfChemicals: 0,
                depositBookingDetails: [],
                liquidWasteBookingDetails: [],
                solidWasteBookingDetails: [],
            });
            const getUserDetailResponse = await UserApi.getUserDetail();
            if (getUserDetailResponse?.status === 200) {
                const user: UserDetail = getUserDetailResponse?.data;
                const result = await BookingApi.getBookingForLaboratoryCheck(date, user.laboId);
                if (result?.status === 200) {
                    const bookingEntities: LiquidWasteBooking = result.data;
                    const bookings = convertBooking(bookingEntities);
                    if (bookings.id !== "") {
                        // 予約がある場合
                        setTargetBookingForLaboratoryCheck(bookings);
                        // setOpenForLaboratoryCautionsDialog(true);

                        // ローディング終了
                        // setLoading(false);
                        // return;
                    }
                }
            }

            // 予約情報を取得
            const result = await BookingApi.getBooking(startViewDate, endViewDate);
            if (result?.status === 200) {
                const bookingEntities: LiquidWasteBooking[] = Array.from(result?.data.liquidWasteBookings);
                const bookings = convertBookings(bookingEntities);
                setBookings(bookings, date);
            }
            setTargetBooking(createInitializeBooking(date));
            setOpenForSubscribeDialog(true);

            // ローディング終了
            setLoading(false);
        };
        executeAboutBookingdetail();
    };

    /**
     * 予約済みイベントがクリックされた場合に行う処理
     * @param arg 選択されたイベント情報
     */
    const handleCLick = async (arg: any) => {
        // ローディング開始
        setLoading(true);
        const curDate = new Date();

        // 休日の場合は何もしない
        if (arg.event.groupId === CommonConstants.GROUP_ID_HOLIDAY) {
            // ローディング終了
            setLoading(false);
            return;
        }
        if (
            (arg.event.groupId !== CommonConstants.GROUP_ID_FREE &&
                new Date(curDate.setDate(curDate.getDate() + 1)).getDate() === arg.event.start.getDate() &&
                curDate.getHours() < ConfigGetter.getNum(ConfigKey.BOOKING_BOOKINGABLE_LIMIT)) ||
            curDate < arg.event.start
        ) {
            // 当日以降の予約であり、イベント押下時が予約前日の12時までの場合（変更・取消選択）
            const event = await BookingApi.getBookingForConfirm(arg.event.id);
            if (event?.status === 200 && event.data.id !== null) {
                const bookingEntities: LiquidWasteBooking = event.data;
                const booking = convertBooking(bookingEntities);
                setTargetBooking(booking);
                setOpenForSelectBookingChangeorDeleteDialog(true);
            }
        } else {
            // 当日以前の予約であり、イベント押下時が予約前日の12時以降の場合（予約確認のみ）
            const event = await BookingApi.getBookingForConfirm(arg.event.id);
            if (event?.status === 200) {
                const bookingEntities: LiquidWasteBooking = event.data;
                const booking = convertBooking(bookingEntities);
                setTargetBooking(booking);
                setOpenForConfirmDialog(true);
            }
        }
        // ローディング終了
        setLoading(false);
    };

    /**
     * 予約可能日時を設定する
     * @param bookings 予約イベント
     * @param date 押下された日付
     */
    const setBookings = (bookings: EventType[], date: Date) => {
        const events: EventType[] = [];
        events.push(...getFreeBookingEvents(bookings, date));
        setEvents(events);
    };

    /**
     * DBから取得した予約情報を、イベント情報として持ち回せるよう詰め替える(予約情報一件の場合)
     * @param bookingEntities DBから取得した予約情報
     * @return 予約イベント
     */
    const convertBooking = (bookingEntities: LiquidWasteBooking): EventType => {
        const booking: EventType = {
            id: bookingEntities.id !== null ? bookingEntities.id.toString() : ``,
            start: new Date(bookingEntities.bookingStartTime),
            end: new Date(bookingEntities.bookingEndTime),
            classNames: [`${BookingUtil.getClassNameByBookingType(bookingEntities.bookingTypeId)}`],
            groupId: CommonConstants.GROUP_ID_BOOKING,
            bookingTypeId: bookingEntities.bookingTypeId,
            status: bookingEntities.status,
            subscriberFaculty: bookingEntities.subscriberFaculty,
            subscriberDepartment: bookingEntities.subscriberDepartment,
            subscriberName: bookingEntities.subscriberName,
            subscriberLaboratoryId: bookingEntities.subscriberLaboratoryId,
            subscriberLaboratoryName: bookingEntities.subscriberLaboratoryName,
            subscriberMailAddress: bookingEntities.subscriberMailAddress,
            subscriberTel: bookingEntities.subscriberTel,
            subscriberMemo: bookingEntities.subscriberMemo,
            numberOfChemicals: bookingEntities.numberOfChemicals,
            depositBookingDetails: bookingEntities.depositBookingDetails,
            liquidWasteBookingDetails: bookingEntities.liquidWasteBookingDetails,
            solidWasteBookingDetails: bookingEntities.solidWasteBookingDetails,
        };
        return booking;
    };

    /**
     * 指定のIDに紐づくイベントを取得
     * @param id イベントID
     * @return イベント
     */
    const getEventById = (id: string): EventType => {
        for (let i = 0; i < events.length; i++) {
            if (id === events[i].id.toString() && id !== ``) {
                return events[i];
            }
        }
        // イベントIDがnullの場合、空のイベントを返却する
        const event: EventType = {
            id: ``,
            start: new Date(),
            end: new Date(),
            classNames: [],
            groupId: ``,
            bookingTypeId: 0,
            status: 0,
            subscriberFaculty: ``,
            subscriberDepartment: ``,
            subscriberName: ``,
            subscriberLaboratoryId: ``,
            subscriberLaboratoryName: ``,
            subscriberMailAddress: ``,
            subscriberTel: ``,
            subscriberMemo: ``,
            numberOfChemicals: 0,
            solidWasteBookingDetails: [],
            liquidWasteBookingDetails: [],
            depositBookingDetails: [],
        };
        return event;
    };

    /**
     * 予約種別名を取得
     * @param id イベントID
     * @return 予約種別名
     */
    const getBookingTypeName = (id: number | undefined): string => {
        let value = ``;
        switch (id) {
            case DbValueConstants.BOOKING_TYPE_ID_LIQUID_WASTE:
                value = CommonConstants.BOOKING_TYPE_NAME_LIQUID_WASTE;
                break;
            case DbValueConstants.BOOKING_TYPE_ID_DEPOSIT:
                value = CommonConstants.BOOKING_TYPE_NAME_DEPOSIT;
                break;
            default:
                break;
        }
        return value;
    };

    /**
     * イベントごとのレンダリング時に行う処理
     *
     * @param info 対象イベント
     */
    const renderEventContent = (info: EventContentArg) => {
        if (getViewMode() !== CommonConstants.VIEWMODE_LIST_WEEK) {
            // リスト表示以外の場合は処理なし
            return;
        }

        if (info.event.id !== "") {
            const event: EventType = getEventById(info.event.id);
            if (event === null) {
                return;
            }

            // リスト表示の場合、予約詳細情報を一覧に追加する
            return (
                <table>
                    <tbody>
                        <tr>
                            <th className={classes.listTable_th}>廃液・付着のみ</th>
                            <th className={classes.listTable_th}>廃薬品</th>
                            <th className={classes.listTable_th}>実験廃棄物回収依頼表</th>
                        </tr>
                        <tr>
                            <td className={`${classes.listTable_td} ${classes.listTable_tdBookingType}`}>
                                {getBookingTypeName(event.bookingTypeId)}
                            </td>
                            <td className={`${classes.listTable_td} ${classes.listTable_tdRequestTable}`}>
                                {event.numberOfChemicals ? event.numberOfChemicals : 0}
                            </td>
                            <td className={`${classes.listTable_td} ${classes.listTable_tdRequestTable}`}>
                                <AssignmentIcon />
                            </td>
                        </tr>
                    </tbody>
                </table>
            );
        }
    };

    /**
     * 表示する日付（期間）が変更された場合に行う処理
     *
     * @param start 表示開始日
     * @param end 表示終了日
     */
    const handleChangeDate = (start: Date, end: Date) => {
        // カレンダーに表示する最初の日にちを判定
        const initialDate: Date = start;
        if (getViewMode() === CommonConstants.VIEWMODE_DAY_GRID_MONTH && start.getDate() !== 1) {
            // 月初が1日でない場合があるので、強制的に表示月の1日を指定
            initialDate.setDate(1);
            initialDate.setMonth(initialDate.getMonth() + 1);
        }
        // カレンダーの表示開始、終了日を設定
        if (start.toString() !== startViewDate.toString()) {
            sessionStorage.setItem(CommonConstants.SESSION_KEY_START_VIEW_DATE, start.toString());
            setStartViewDate(start);
        }
        if (end.toString() !== endViewDate.toString()) {
            sessionStorage.setItem(CommonConstants.SESSION_KEY_END_VIEW_DATE, end.toString());
            setEndViewDate(end);
        }
    };

    /**
     * 次のステップへ移動する処理
     */
    const nextStep = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    /**
     * 前のステップへ移動する処理
     */
    const backStep = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    /**
     * 初期表示時、および表示形式（月、週、日、リスト）が切り替わったタイミングで行う処理
     */
    const viewDidMount = () => {
        if (ref.current === null) {
            setViewMode(CommonConstants.VIEWMODE_DAY_GRID_MONTH);
            return;
        }
        // 予約情報を初期化
        ref.current.getApi().removeAllEvents();
        const viewMode = ref.current.getApi().view.type;
        sessionStorage.setItem(CommonConstants.SESSION_KEY_VIEW_MODE, viewMode);
        setViewMode(viewMode);
    };

    /**
     * 休日かどうか判定
     * @param targetDate 判定対象日時
     * @return 休日の場合true
     */
    const isHoliday = (targetDate: Date): boolean => {
        const date = new Date(targetDate);
        // 土日かどうか
        if (date.getDay() === 0 || date.getDay() === 6) {
            return true;
        }
        // 休日かどうか
        date.setHours(0);
        const holidays: string[] = getHolidays();
        for (let i = 0; i < holidays.length; i++) {
            if (date.getTime() === new Date(holidays[i]).getTime()) {
                return true;
            }
        }
        // 営業日の場合
        return false;
    };

    /**
     * 予約可能日かどうか判定
     * @param targetDate 判定対象日時
     * @return 予約可能日の場合true
     */
    const isBookableDate = (targetDate: Date): boolean => {
        const date = new Date(targetDate);
        const now = new Date();
        const endTime = ConfigGetter.getNum(ConfigKey.BOOKING_END_TIME);
        const bookingableLimit = ConfigGetter.getNum(ConfigKey.BOOKING_BOOKINGABLE_LIMIT);
        const bookingableDays = ConfigGetter.getNum(ConfigKey.BOOKING_BOOKINGABLE_DAYS);
        const bookingableDaysOfWeek = ConfigGetter.get(ConfigKey.BOOKING_BOOKINGABLE_DAYS_OF_WEEK);
        // 最小予約可能日を設定
        const min = new Date(date);
        min.setDate(min.getDate() - 1);
        min.setHours(bookingableLimit);
        min.setMinutes(0);
        min.setSeconds(0);
        min.setMilliseconds(0);
        // 最大予約可能日を設定
        const max = new Date(now);
        max.setDate(max.getDate() + bookingableDays);
        max.setHours(endTime);
        max.setMinutes(0);
        max.setSeconds(0);
        max.setMilliseconds(0);

        return (
            !isHoliday(date) &&
            date.getTime() < max.getTime() &&
            now.getTime() < min.getTime() &&
            bookingableDaysOfWeek.includes(date.getDay().toString())
        );
    };

    /**
     * 日毎に行う処理
     * @param arg イベント
     */
    const handleDayCellClassNames = (arg: any) => {
        // 月表示の場合、休日の日に色を付ける
        if (arg.view.type === CommonConstants.VIEWMODE_DAY_GRID_MONTH) {
            if (isHoliday(arg.date)) {
                return [classes.holiday];
            }
            // 予約可能日に色をつける
            if (isBookableDate(arg.date)) {
                return [classes.bookable_days];
            }
        }
        return [];
    };

    /**
     * 予約の「登録」ボタン押下時の処理
     */
    const createBooking = () => {
        const execute = async () => {
            // ローディング開始
            setLoading(true);

            // 登録
            let result: any = await BookingApi.createBooking(targetBooking);
            if (result.data?.success) {
                const contents: any = await MailApi.getMailMessage();
                if (contents?.status === 200) {
                    // メール送信
                    const mail: Mail = {
                        id: Number(result.data.id),
                        to:
                            targetBooking.subscriberMailAddress !== undefined
                                ? targetBooking.subscriberMailAddress
                                : ``,
                        subject: contents.data.createSubject,
                        message: contents.data.createMessage,
                    };
                    result = await MailApi.sendMail(mail);

                    // 更新内容を反映
                    setEvents(getRemoveEventById(freeEventId));
                    nextStep();
                }
            } else {
                // エラーメッセージ表示
                if (result.data !== undefined) {
                    if (result.data?.errorMessage !== null && result.data?.errorMessage !== `laboratoryError`) {
                        setErrorMessage(result.data?.errorMessage);
                    } else {
                        // 選択日に、ログインしている研究室での予約がある場合
                        const result = await BookingApi.getBookingForLaboratoryCheck(
                            targetBooking.start,
                            targetBooking.subscriberLaboratoryId
                        );
                        if (result?.status === 200) {
                            const bookingEntities: LiquidWasteBooking = result.data;
                            const bookings = convertBooking(bookingEntities);
                            setTargetBookingForLaboratoryCheck(bookings);
                            setOpenForLaboratoryCautionsDialog(true);
                        }
                        setErrorMessage(``);
                    }
                } else {
                    if (result.errors?.errorMessage !== null) {
                        setErrorMessage(result.errors?.errorMessage);
                    }
                }
            }

            // ローディング終了
            setLoading(false);
        };
        execute();
    };

    /**
     * 予約の「変更」ボタン押下時の処理
     */
    const updateBooking = () => {
        const execute = async () => {
            // ローディング開始
            setLoading(true);

            // 更新
            let result: any = await BookingApi.updateBooking(targetBooking);
            if (result.data?.success) {
                const contents: any = await MailApi.getMailMessage();
                if (contents?.status === 200) {
                    // メール送信
                    const mail: Mail = {
                        id: Number(targetBooking.id),
                        to:
                            targetBooking.subscriberMailAddress !== undefined
                                ? targetBooking.subscriberMailAddress
                                : ``,
                        subject: contents.data.updateSubject,
                        message: contents.data.updateMessage,
                    };
                    result = await MailApi.sendMail(mail);

                    // 更新内容を反映
                    const newEvents = getRemoveEventById(targetBooking.id);
                    newEvents.push(targetBooking);
                    setEvents(newEvents);
                    nextStep();
                }
            } else {
                // エラーメッセージ表示
                if (result.data !== undefined) {
                    if (result.data?.errorMessage !== null) {
                        setErrorMessage(result.data?.errorMessage);
                    }
                } else {
                    if (result.errors?.errorMessage !== null) {
                        setErrorMessage(result.errors?.errorMessage);
                    }
                }
            }

            // ローディング終了
            setLoading(false);
        };
        execute();
    };

    /**
     * 予約の「取消」ボタン押下時の処理
     */
    const deleteBooking = () => {
        const execute = async () => {
            // ローディング開始
            setLoading(true);

            // 削除
            let result: any = await BookingApi.deleteBooking(parseInt(targetBooking.id), targetBooking.bookingTypeId);
            if (result.data?.success) {
                const contents: any = await MailApi.getMailMessage();
                if (contents?.status === 200) {
                    // メール送信
                    const mail: Mail = {
                        id: Number(targetBooking.id),
                        to:
                            targetBooking.subscriberMailAddress !== undefined
                                ? targetBooking.subscriberMailAddress
                                : ``,
                        subject: contents.data.deleteSubject,
                        message: contents.data.deleteMessage,
                    };
                    result = await MailApi.sendMail(mail);

                    // 更新内容を反映
                    setEvents(getRemoveEventById(targetBooking.id));
                    setOpenForConfirmModal(false);
                    setOpenForSelectBookingChangeorDeleteDialog(false);
                }
            } else {
                // エラーメッセージ表示
                if (result.data !== undefined) {
                    if (result.data?.errorMessage !== null) {
                        setErrorMessage(result.data?.errorMessage);
                    }
                } else {
                    if (result.errors?.errorMessage !== null) {
                        setErrorMessage(result.errors?.errorMessage);
                    }
                }
            }

            // ローディング終了
            setLoading(false);
        };
        execute();
    };

    /**
     * 対象のイベントIDに紐づくイベントを削除
     * @param eventId イベントID
     * @return 対象のイベントを削除したイベント情報
     */
    const getRemoveEventById = (eventId: string) => {
        return events.filter((row) => {
            return row.id !== eventId;
        });
    };

    /**
     * カレンダー設定用にイベントを詰め替える
     * @return イベント情報
     */
    const convertEventsForCalendar = () => {
        const convertedEvents: any[] = [];
        events.forEach((event) => {
            convertedEvents.push({
                id: event.id,
                start: event.start,
                end: event.end,
                classNames: event.classNames,
                groupId: event.groupId,
                extendedProps: event.extendedProps,
                bookingTypeId: event.bookingTypeId,
                status: event.status,
                subscriberFaculty: event.subscriberFaculty,
                subscriberDepartment: event.subscriberDepartment,
                subscriberName: event.subscriberName,
                subscriberLaboratoryId: event.subscriberLaboratoryId,
                subscriberLaboratoryName: event.subscriberLaboratoryName,
                subscriberMailAddress: event.subscriberMailAddress,
                subscriberTel: event.subscriberTel,
                subscriberMemo: event.subscriberMemo,
                numberOfChemicals: event.numberOfChemicals,
                liquidWasteBookingDetails: event.liquidWasteBookingDetails,
                solidWasteBookingDetails: event.solidWasteBookingDetails,
                depositBookingDetails: event.depositBookingDetails,
            });
        });
        return convertedEvents;
    };

    /**
     * 予約時入力チェック（予約時間）
     */
    const checkBookingTime = () => {
        if (targetBooking.bookingTypeId !== 0) {
            nextStep();
            setErrorMessage(``);
        } else {
            setErrorMessage(`予約時間を選択してください。`);
        }
    };

    /**
     * 予約時入力チェック（予約者情報）
     */
    const checkSubscriberInfo = async () => {
        const result: any = await BookingApi.checkSubscriberInfo(targetBooking);
        if (result.data?.success) {
            nextStep();
            setErrorMessage(``);
        } else {
            // エラーメッセージ表示
            if (result.data !== undefined) {
                if (result.data?.errorMessage !== null) {
                    setErrorMessage(result.data?.errorMessage);
                }
            } else {
                if (result.errors?.errorMessage !== null) {
                    setErrorMessage(result.errors?.errorMessage);
                }
            }
        }
    };

    /**
     * 予約時入力チェック（廃液情報）
     */
    const checkBookingDetail = async () => {
        const result: any = await BookingApi.checkBookingDetail(targetBooking);
        if (result.data?.success) {
            nextStep();
            setErrorMessage(``);
        } else {
            // エラーメッセージ表示
            if (result.data !== undefined) {
                if (result.data?.errorMessage !== null) {
                    setErrorMessage(result.data?.errorMessage);
                }
            } else {
                if (result.errors?.errorMessage !== null) {
                    setErrorMessage(result.errors?.errorMessage);
                }
            }
        }
    };

    return (
        <div className={classes.root}>
            <SubscribeDialog
                isOpen={isOpenForSubscribeDialog}
                onClickCancel={() => {
                    setErrorMessage(``);
                    setOpenForSubscribeDialog(false);
                }}
                onClickAdd={() => {
                    createBooking();
                }}
                onClickChange={() => {
                    updateBooking();
                }}
                booking={targetBooking}
                events={events}
                handleNext={() => {
                    if (activeStep === CommonConstants.SELECT_BOOKING_TIME_DIAOLG) {
                        checkBookingTime();
                        // 予約種別「付着物のみ」が選択された場合は「廃液廃棄物」「固形廃棄物」の詳細を削除する
                        if (targetBooking.bookingTypeId === DbValueConstants.BOOKING_TYPE_ID_DEPOSIT) {
                            targetBooking.liquidWasteBookingDetails.splice(0);
                            targetBooking.solidWasteBookingDetails.splice(0);
                        }
                    } else if (activeStep === CommonConstants.SUBSCRIBER_INFO_DIALOG) {
                        checkSubscriberInfo();
                    } else if (activeStep === CommonConstants.WASTED_LIQUID_SUBSCRIBE_DIALOG) {
                        // ポリ容器の回収本数が全て0の情報は予約対象外として除外する
                        targetBooking.liquidWasteBookingDetails = targetBooking.liquidWasteBookingDetails.filter(
                            (detail) => {
                                return (
                                    detail.numberOfContainer1 !== 0 ||
                                    detail.numberOfContainer2 !== 0 ||
                                    detail.numberOfContainer3 !== 0 ||
                                    detail.numberOfContainer4 !== 0 ||
                                    detail.numberOfContainer5 !== 0
                                );
                            }
                        );
                        // 個数と容量が0の情報は予約対象外として除外する
                        targetBooking.solidWasteBookingDetails = targetBooking.solidWasteBookingDetails.filter(
                            (detail) => {
                                return detail.capacity !== 0 || detail.numberOfSolidWastes !== 0;
                            }
                        );
                        // 付着物の個数が0の情報は予約対象外として除外する
                        targetBooking.depositBookingDetails = targetBooking.depositBookingDetails.filter((detail) => {
                            return detail.numberOfSolidWastes !== 0;
                        });
                        checkBookingDetail();
                    }
                }}
                handleBack={() => {
                    setErrorMessage(``);
                    backStep();
                }}
                step={activeStep}
                setNewBooking={(id: string) => {
                    setFreeEventId(id);
                }}
                targetBookingForLaboratoryCheck={targetBookingForLaboratoryCheck}
                onClickLinkDoubleBooking={() => {
                    sessionStorage.setItem(CommonConstants.SESSION_KEY_VIEW_MODE, CommonConstants.VIEWMODE_LIST_WEEK);
                    history.go(0);
                    // setOpenForLaboratoryCautionsDialog(false);
                }}
                errorMessage={errorMessage}
                isLoading={isLoading}
                setLoading={setLoading}
            />
            <SelectBookingChangeOrDeleteDialog
                isOpen={isOpenForSelectBookingChangeorDeleteDialog}
                onClickCancel={() => {
                    setOpenForSelectBookingChangeorDeleteDialog(false);
                }}
                onClickDelete={() => {
                    setOpenForConfirmModal(true);
                    setMessageForConfirmModal("この予約を取り消しますか？");
                }}
                onClickNextToBookingChange={() => {
                    setOpenForSubscribeDialog(true);
                    setOpenForSelectBookingChangeorDeleteDialog(false);
                    setActiveStep(CommonConstants.WASTED_LIQUID_SUBSCRIBE_DIALOG);
                }}
                booking={targetBooking}
                setLoading={setLoading}
            />
            <ConfirmDialog
                isOpen={isOpenForConfirmDialog}
                onClickCancel={() => {
                    setOpenForConfirmDialog(false);
                }}
                booking={targetBooking}
                setLoading={setLoading}
            />
            <LaboratoryCautionsDialog
                isOpen={isOpenForLaboratoryCautionsDialog}
                onClickCancel={() => {
                    setOpenForLaboratoryCautionsDialog(false);
                }}
                booking={targetBookingForLaboratoryCheck}
                onClickLink={() => {
                    sessionStorage.setItem(CommonConstants.SESSION_KEY_VIEW_MODE, CommonConstants.VIEWMODE_LIST_WEEK);
                    history.go(0);
                    setOpenForLaboratoryCautionsDialog(false);
                }}
            />
            <ConfirmModal
                isOpen={isOpenForConfirmModal}
                onClickNo={() => {
                    setOpenForConfirmModal(false);
                }}
                onClickYes={() => {
                    deleteBooking();
                }}
                message={messageForConfirmModal}
            />
            <div className={classes.cautions_select}>※希望日を選択してください</div>
            <div className={classes.cautions}>
                <ul>
                    <li>
                        <div className={classes.display}>
                            <div className={classes.box}></div>：予約可能日
                        </div>
                    </li>
                    <li>予約締切：廃液回収日前日の{ConfigGetter.get(ConfigKey.BOOKING_DEADLINE)}まで</li>
                    <li>
                        <a href={ConfigGetter.get(ConfigKey.HP_LINK)} target="_blank" rel="noopener noreferrer">
                            センターHPリンク
                        </a>
                    </li>
                    <li>
                        <a href={ConfigGetter.get(ConfigKey.SOLVENT_LINK)} target="_blank" rel="noopener noreferrer">
                            汲出し・持出しオンライン予約リンク
                        </a>
                    </li>
                </ul>
            </div>
            <Loading isLoading={isLoading} />
            <p className="error">{errorMessage}</p>
            <FullCalendar
                locale={ConfigGetter.get(ConfigKey.CALENDAR_LOCALE)} // ロケール設定。
                plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin, listPlugin]} // 週表示、月表示、日付等のクリックを可能にするプラグインを設定。
                initialView={viewMode} // カレンダーの初期表示設定
                initialDate={initialDate} // カレンダーが最初に読み込まれたときに表示される最初の日付
                slotDuration={ConfigGetter.get(ConfigKey.CALENDAR_SLOTDURATION)} // 日表示した時の時間軸の単位。
                selectable={false} // 日付選択を可能にする。interactionPluginが有効になっている場合のみ。
                navLinks={true} // カレンダー内の日付クリックで日表示に遷移するかどう
                // ビジネス時間の設定。（0:日曜 〜 7:土曜）
                businessHours={[
                    {
                        // 午前
                        daysOfWeek: ConfigGetter.get(ConfigKey.CALENDAR_DAYS_OF_WEEK),
                        startTime: ConfigGetter.get(ConfigKey.CALENDAR_START_TIME_AM),
                        endTime: ConfigGetter.get(ConfigKey.CALENDAR_END_TIME_AM),
                    },
                    {
                        // 午後
                        daysOfWeek: ConfigGetter.get(ConfigKey.CALENDAR_DAYS_OF_WEEK),
                        startTime: ConfigGetter.get(ConfigKey.CALENDAR_START_TIME_PM),
                        endTime: ConfigGetter.get(ConfigKey.CALENDAR_END_TIME_PM),
                    },
                ]}
                weekends={true} // 週末を強調表示する。
                views={{
                    dayGridMonth: {
                        titleFormat: (obj: any) => {
                            return (
                                obj.start.year + `/` + (obj.start.month + 1) + `/` + obj.start.day + `-` + obj.end.day
                            );
                        },
                    },
                    timeGridDay: {
                        titleFormat: (obj: any) => {
                            return obj.date.year + `/` + (obj.date.month + 1) + `/` + obj.date.day;
                        },
                    },
                    listMonth: {
                        titleFormat: (obj: any) => {
                            return (
                                obj.start.year + `/` + (obj.start.month + 1) + `/` + obj.start.day + `-` + obj.end.day
                            );
                        },
                    },
                    listWeek: {
                        titleFormat: (obj: any) => {
                            return (
                                obj.start.year + ` / ` + (obj.start.month + 1) + ` / ` + obj.start.day + ` - ` +
                                (obj.start.year !== obj.end.year ? obj.end.year + ` / ` :  ``) +
                                (obj.start.month !== obj.end.month ? (obj.end.month + 1) + ` / ` :  ``) + obj.end.day
                            );
                        },
                    },
                }}
                titleFormat={{
                    // タイトルのフォーマット
                    year: `numeric`,
                    month: `short`,
                    day: `numeric`,
                    // weekday: `short`,
                }}
                listDayFormat={{
                    year: `numeric`,
                    month: `short`,
                    day: `numeric`,
                    weekday: `short`,
                }}
                slotLabelFormat={{
                    // 週、日表示の時間フォーマット
                    hour: `numeric`,
                    minute: `2-digit`,
                }}
                dayCellContent={(el: any) => {
                    el.dayNumberText = el.dayNumberText.replace(`日`, ``);
                }}
                listDaySideFormat={false}
                headerToolbar={{
                    // カレンダーのヘッダー設定
                    start: `title,prev,today,next`,
                    center: ``,
                    end: `${CommonConstants.VIEWMODE_DAY_GRID_MONTH},${CommonConstants.VIEWMODE_LIST_WEEK}`,
                }}
                buttonText={{
                    today: `今日`,
                    month: `月`,
                    day: `日`,
                    list: `予約一覧`,
                }}
                slotEventOverlap={false} // イベントを重ねて表示するかどうか
                allDaySlot={false} // allDayを非表示にする
                stickyHeaderDates={true} // スクロール中に日付を固定する
                slotMinTime={ConfigGetter.get(ConfigKey.CALENDAR_START_TIME_AM)} // 週表示の開始時間
                slotMaxTime={ConfigGetter.get(ConfigKey.CALENDAR_END_TIME_PM)} // 週表示の終了時間
                eventTimeFormat={{
                    // イベントの時間表示方法の設定
                    hour: `2-digit`,
                    minute: `2-digit`,
                    meridiem: false,
                }}
                contentHeight="auto"
                datesSet={(arg: DatesSetArg) => {
                    handleChangeDate(arg.start, arg.end);
                }}
                ref={ref}
                events={convertEventsForCalendar()}
                eventClick={handleCLick}
                eventContent={renderEventContent}
                viewDidMount={() => viewDidMount()}
                dayCellClassNames={handleDayCellClassNames}
                // 空白押下時の処理
                dateClick={handleDateClick}
                // 日付押下時の処理
                navLinkDayClick={handleDateClick}
            />
        </div>
    );
};
